import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getAllPositions, selectPosition } from '@store/masterData/positionSlice';
import { ReactText } from 'react';
import { toast } from 'react-toastify';
import { stringResources } from 'src/assets/config/stringResources';
import { RootState } from 'src/redux/store';
import { ContractWorkByRequest, ContractWorkByResponse, ContractWorkBysClient } from 'src/utility/apis/crewPayApiClient';
import axios from 'src/utility/apis/crewPayAxios';
import { Equal, handleError, showNotFoundErrorNotification } from 'src/utility/utils';
import { WorkbyDataModel } from 'src/views/contract/vesselContract/Import/DataTypes';

export interface ContractWorkBy extends ContractWorkByResponse { }

export interface State {
    isLoading: boolean;
    error: boolean;
    contractWorkByList: ContractWorkBy[];
}

const initialState: State = {
    isLoading: true,
    error: false,
    contractWorkByList: [],
};

const contractWorkBySlice = createSlice({
    name: 'contractWorkBy',
    initialState,
    reducers: {
        startLoading: (state) => {
            state.isLoading = true;
        },
        hasError: (state, action) => {
            state.error = action.payload;
            state.isLoading = false;
        },
        getContractWorkByListSuccess: (state, action: PayloadAction<ContractWorkBy[]>) => {
            state.contractWorkByList = action.payload;
            state.isLoading = false;
        },
        addNewContractWorkBySuccess: (state, action: PayloadAction<ContractWorkBy>) => {
            const newItem = action.payload;
            state.contractWorkByList = [newItem, ...state.contractWorkByList];
            toast.success(stringResources.REDUX_SLICE.CREATE_SUCCESS);
            state.isLoading = false;
        },
        updateContractWorkBySuccess: (state, action: PayloadAction<ContractWorkBy>) => {
            const updatedItem = action.payload;
            const index = state.contractWorkByList.findIndex(item => item.contractWorkById === updatedItem.contractWorkById);
            state.contractWorkByList = [...state.contractWorkByList.slice(0, index), updatedItem, ...state.contractWorkByList.slice(index + 1)];
            toast.success(stringResources.REDUX_SLICE.UPDATE_SUCCESS);
            state.isLoading = false;
        },
        deleteContractWorkBySuccess: (state, action: PayloadAction<number>) => {
            const contractWorkById = action.payload;
            state.contractWorkByList = state.contractWorkByList.filter(item => item.contractWorkById !== contractWorkById);
            toast.success(stringResources.REDUX_SLICE.DELETE_SUCCESS);
            state.isLoading = false;
        },
    },
});

// Reducer
export default contractWorkBySlice.reducer

// Actions
export const {
    startLoading,
    hasError,
    getContractWorkByListSuccess,
    addNewContractWorkBySuccess,
    updateContractWorkBySuccess,
    deleteContractWorkBySuccess
} = contractWorkBySlice.actions;

// for useSelector
export const selectContractWorkBy = (state: RootState) => state.contractWorkBy as State;

// ----------------------------------------------------------------------

export function getContractWorkByList(vesselContractId: number) {
    return async (dispatch, getState) => {
        dispatch(contractWorkBySlice.actions.startLoading());
        try {
            const client = new ContractWorkBysClient(undefined, axios);
            const response = await client.getByVesselContractId(vesselContractId);
            dispatch(contractWorkBySlice.actions.getContractWorkByListSuccess(response));
            return response;
        } catch (error) {
            dispatch(contractWorkBySlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function createContractWorkBy(contractWorkBy: ContractWorkBy) {
    return async (dispatch, getState) => {
        dispatch(contractWorkBySlice.actions.startLoading());
        try {
            const { contractWorkById, ...payload } = contractWorkBy;
            const request = payload as ContractWorkByRequest;
            const client = new ContractWorkBysClient(undefined, axios);
            const response = await client.create(request);
            dispatch(contractWorkBySlice.actions.addNewContractWorkBySuccess(response));
        } catch (error) {
            dispatch(contractWorkBySlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function createContractWorkByBatch(vesselContractId: number, workbyData: WorkbyDataModel[],
    toastId: ReactText, currentProgress: number, totalProgress: number) {
    return async (dispatch, getState) => {
        dispatch(contractWorkBySlice.actions.startLoading());
        try {
            await dispatch(getAllPositions());
            const { positionList } = selectPosition(getState());

            const client = new ContractWorkBysClient(undefined, axios);
            for (const d of workbyData) {
                const positionId = positionList.find(p => Equal(p.positionCode, d.PositionCode))?.positionId;
                if (positionId) {
                    const request = {
                        vesselContractId,
                        positionId,
                        description: d.Description,
                        amount: d.Amount
                    } as ContractWorkByRequest;
                    await client.create(request);
                    currentProgress += 1;
                    toast.update(toastId, { render: `${stringResources.IMPORT.WORKBY_IMPORTING} import in progressing. (${currentProgress}/${totalProgress})`, progress: currentProgress / totalProgress });
                } else {
                    showNotFoundErrorNotification(d.PositionCode);
                }
            }
        } catch (error) {
            handleError(error);
        }
    };
}

export function updateContractWorkBy(contractWorkBy: ContractWorkBy) {
    return async (dispatch, getState) => {
        dispatch(contractWorkBySlice.actions.startLoading());
        try {
            const { contractWorkById, ...payload } = contractWorkBy;
            const request = payload as ContractWorkByRequest;
            const client = new ContractWorkBysClient(undefined, axios);
            const response = await client.edit(contractWorkById, request);
            dispatch(contractWorkBySlice.actions.updateContractWorkBySuccess(response));
        } catch (error) {
            dispatch(contractWorkBySlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function deleteContractWorkBy(contractWorkById: number) {
    return async (dispatch, getState) => {
        dispatch(contractWorkBySlice.actions.startLoading());
        try {
            const client = new ContractWorkBysClient(undefined, axios);
            await client.delete(contractWorkById);
            dispatch(contractWorkBySlice.actions.deleteContractWorkBySuccess(contractWorkById));
        } catch (error) {
            dispatch(contractWorkBySlice.actions.hasError(true));
            handleError(error);
        }
    };
}